import { useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { formatDate } from "../../utils/dateAndTime";
import DeleteFight from "../../components/modals/DeleteFight";
import UpdateFight from "../../components/modals/UpdateFight";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getFightByID, voteForFight, removeFightVote } from "../../http/auth";
import { useAuthStore } from "../../store/authStore";
import { toast } from "react-toastify";
import CommentsSection from "../../components/comments/CommentsSection";
import { GreatFightRating } from "../../components/fights/GreatFightRating";
import playerSilhouette from "../../assets/player-silhoutte.png";

const extractVideoId = (url) => {
  if (!url) return null;
  const regex =
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\s*[^\/\n\s]+\/|(?:v|e(?:mbed)?)\/|\S*?[\?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

const PlayerPanel = ({ player, isVoted, onVote, isPending }) => {
  const navigate = useNavigate();
  const [imgError, setImgError] = useState(false);

  return (
    <div className="flex flex-col items-center flex-1">
      {/* Player Image */}
      <button
        onClick={() => navigate(`/player/${player.PlayerID}`)}
        className="group relative mb-3"
      >
        <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full overflow-hidden ring-3 ring-flyers-orange ring-offset-2 ring-offset-flyers-charcoal transition-all duration-200 group-hover:ring-flyers-orange-light group-hover:shadow-orange-glow">
          <img
            src={player.playerImageFile && !imgError ? player.playerImageFile : playerSilhouette}
            alt={`${player.firstName} ${player.lastName}`}
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        </div>
      </button>

      {/* Player Name */}
      <button
        onClick={() => navigate(`/player/${player.PlayerID}`)}
        className="hover:text-flyers-orange transition-colors"
      >
        <h2 className="font-heading text-xl sm:text-2xl font-black uppercase tracking-wide text-white hover:text-flyers-orange transition-colors">
          {player.firstName} {player.lastName}
        </h2>
      </button>

      {/* W-L-D Record */}
      <p className="text-flyers-mid-gray text-sm mt-1 font-body">
        <span className="text-green-500">{player.wins ?? 0}W</span>
        {" - "}
        <span className="text-red-400">{player.losses ?? 0}L</span>
        {" - "}
        <span className="text-flyers-light-gray">{player.draws ?? 0}D</span>
      </p>

      {/* Vote Button */}
      <button
        onClick={onVote}
        disabled={isPending}
        aria-pressed={isVoted}
        aria-label={
          isVoted
            ? `Remove your vote for ${player.lastName}`
            : `Vote for ${player.lastName}`
        }
        title={isVoted ? "Click to remove your vote" : undefined}
        className={`mt-4 px-6 py-2 rounded font-heading font-bold uppercase tracking-wide text-sm transition-all duration-200 ${
          isVoted
            ? "bg-flyers-orange text-white shadow-orange-glow hover:bg-flyers-orange-light"
            : "border border-flyers-dark-gray text-flyers-light-gray hover:border-flyers-orange hover:text-white bg-flyers-charcoal"
        } ${isPending ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        {isVoted ? (
          <span className="flex items-center gap-1.5">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Voted
          </span>
        ) : (
          `Vote ${player.lastName}`
        )}
      </button>
    </div>
  );
};

export const Fight = () => {
  const navigate = useNavigate();
  const { fightID } = useParams();
  const { user } = useAuthStore();

  const [isOpenDeleteModal, setOpenDeleteModal] = useState(false);
  const [isOpenEditModal, setOpenEditModal] = useState(false);
  const queryClient = useQueryClient();

  const { data: fightData, isLoading } = useQuery({
    queryKey: ["fight", fightID],
    queryFn: () => getFightByID(fightID),
  });

  const fight = fightData?.data;

  const userVote = useMemo(() => {
    return fight?.Votes?.find((v) => v.userId === user?.UserID) || null;
  }, [fight, user]);

  const userRating = useMemo(() => {
    return fight?.Ratings?.find((r) => r.userId === user?.UserID)?.rating ?? null;
  }, [fight, user]);

  const voteCounts = useMemo(() => {
    return {
      player1:
        fight?.Votes?.filter(
          (v) => v.voteType === "PLAYER" && v.playerId === fight?.Player1Details?.PlayerID
        ).length || 0,
      player2:
        fight?.Votes?.filter(
          (v) => v.voteType === "PLAYER" && v.playerId === fight?.Player2Details?.PlayerID
        ).length || 0,
      draw: fight?.Votes?.filter((v) => v.voteType === "DRAW").length || 0,
    };
  }, [fight]);

  const totalVotes = voteCounts.player1 + voteCounts.player2 + voteCounts.draw;

  const isDrawVoted = userVote?.voteType === "DRAW";

  const voteMutation = useMutation({
    mutationFn: (payload) =>
      payload.remove ? removeFightVote(payload) : voteForFight(payload),
    // Returned so the mutation stays pending until the refetch lands. Without
    // it the buttons re-enable while still showing the old selection, letting a
    // fast second click act on state the server has already changed.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["fight", fightID] }),
    onError: () => toast.error("Failed to update vote", { autoClose: 5000 }),
  });

  const handleVote = (target) => {
    if (!user) return toast.error("You must be logged in to vote", { autoClose: 5000 });

    const isDraw = target.voteType === "DRAW";
    const alreadyVoted = isDraw
      ? isDrawVoted
      : userVote?.voteType === "PLAYER" && userVote?.playerId === target.playerId;

    if (alreadyVoted) {
      return voteMutation.mutate({ id: fightID, remove: true });
    }

    voteMutation.mutate({ id: fightID, ...target });
  };

  const videoId = extractVideoId(fight?.videoLink);

  // p2 is derived rather than rounded so the three segments always fill the bar
  const p1Pct = totalVotes > 0 ? Math.round((voteCounts.player1 / totalVotes) * 100) : 0;
  const drawPct = totalVotes > 0 ? Math.round((voteCounts.draw / totalVotes) * 100) : 0;
  const p2Pct = totalVotes > 0 ? 100 - p1Pct - drawPct : 0;

  if (isLoading)
    return (
      <div className="text-white p-6 bg-flyers-black min-h-screen flex items-center justify-center">
        <div className="text-flyers-mid-gray font-heading text-xl uppercase tracking-wide">Loading fight...</div>
      </div>
    );

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8 bg-flyers-black text-white">
      {fight && (
        <>
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb / Context Bar */}
            <div className="flex items-center gap-2 text-sm font-body text-flyers-mid-gray mt-4">
              <Link
                to={`/league/${fight.Game?.LeagueDetails?.LeagueID}`}
                className="hover:text-flyers-orange transition-colors"
              >
                {fight.Game?.LeagueDetails?.name}
              </Link>
              <span>/</span>
              {fight.SeasonDetails && (
                <>
                  <span>{fight.SeasonDetails.season}</span>
                  <span>/</span>
                </>
              )}
              <Link
                to={`/game/${fight.Game?.GameID}`}
                className="hover:text-flyers-orange transition-colors"
              >
                {formatDate(fight.Game?.date)}
              </Link>
              {fight.fightType && (
                <span className="ml-2 px-2 py-0.5 rounded-full bg-flyers-dark-gray text-flyers-orange text-xs font-heading uppercase tracking-wide">
                  {fight.fightType}
                </span>
              )}
            </div>

            {/* Fight Card */}
            <div className="relative mt-6 bg-flyers-charcoal border border-flyers-dark-gray rounded-lg p-6 sm:p-8 shadow-lg">
              {/* Admin Edit & Delete Buttons */}
              {user?.role === "admin" && (
                <div className="absolute top-4 right-4 flex gap-1">
                  <button
                    onClick={() => setOpenEditModal(true)}
                    className="p-2 text-flyers-mid-gray hover:text-flyers-orange transition-colors rounded hover:bg-flyers-dark-gray"
                    title="Edit fight"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setOpenDeleteModal(true)}
                    className="p-2 text-flyers-mid-gray hover:text-red-500 transition-colors rounded hover:bg-flyers-dark-gray"
                    title="Delete fight"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Player Panels + VS */}
              <div className="flex flex-col md:flex-row items-center gap-6 md:gap-4">
                <PlayerPanel
                  player={fight.Player1Details}
                  isVoted={userVote?.playerId === fight.Player1Details.PlayerID}
                  onVote={() => handleVote({ playerId: fight.Player1Details.PlayerID })}
                  isPending={voteMutation.isPending}
                />

                {/* VS Divider + Draw.
                    On md+ the column stretches to the panels' height so the
                    draw button lands on the same line as the vote buttons. */}
                <div className="flex flex-col items-center px-4 md:self-stretch">
                  <span className="font-heading text-3xl sm:text-4xl font-black text-flyers-mid-gray tracking-widest md:flex-1 md:flex md:items-center">
                    VS
                  </span>
                  <button
                    onClick={() => handleVote({ voteType: "DRAW" })}
                    disabled={voteMutation.isPending}
                    aria-pressed={isDrawVoted}
                    aria-label={
                      isDrawVoted ? "Remove your draw vote" : "Vote for a draw"
                    }
                    title={isDrawVoted ? "Click to remove your vote" : undefined}
                    className={`mt-3 md:mt-0 px-6 py-2 rounded font-heading font-bold uppercase tracking-wide text-sm transition-all duration-200 ${
                      isDrawVoted
                        ? "bg-flyers-mid-gray text-white hover:bg-flyers-light-gray hover:text-flyers-charcoal"
                        : "border border-flyers-dark-gray text-flyers-mid-gray hover:border-flyers-light-gray hover:text-flyers-light-gray bg-flyers-charcoal"
                    } ${voteMutation.isPending ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    Draw
                  </button>
                </div>

                <PlayerPanel
                  player={fight.Player2Details}
                  isVoted={userVote?.playerId === fight.Player2Details.PlayerID}
                  onVote={() => handleVote({ playerId: fight.Player2Details.PlayerID })}
                  isPending={voteMutation.isPending}
                />
              </div>

              {/* Vote Result Bar */}
              <div className="mt-8">
                {totalVotes > 0 ? (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm font-heading font-bold uppercase tracking-wide">
                      <span className="text-flyers-orange">{p1Pct}%</span>
                      <span className="text-flyers-mid-gray">
                        {voteCounts.draw > 0 && `${drawPct}% draw · `}
                        {totalVotes} vote{totalVotes !== 1 ? "s" : ""}
                      </span>
                      <span className="text-flyers-light-gray">{p2Pct}%</span>
                    </div>
                    <div className="w-full h-2.5 bg-flyers-dark-gray rounded-full overflow-hidden flex">
                      <div
                        className="bg-flyers-orange transition-all duration-500 ease-out"
                        style={{ width: `${p1Pct}%` }}
                      />
                      <div
                        className="bg-flyers-mid-gray transition-all duration-500 ease-out"
                        style={{ width: `${drawPct}%` }}
                      />
                      <div
                        className="bg-flyers-light-gray transition-all duration-500 ease-out"
                        style={{ width: `${p2Pct}%` }}
                      />
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-flyers-mid-gray text-sm font-body">No votes yet</p>
                )}
              </div>

              <GreatFightRating
                fightID={fightID}
                average={fight.computedRatingAverage}
                count={fight.computedRatingCount ?? 0}
                userRating={userRating}
              />
            </div>

            {/* Fight Details Strip */}
            <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm font-body text-flyers-mid-gray">
              {fight.time && <span>{fight.time}</span>}
              {fight.time && fight.description && <span className="text-flyers-dark-gray">|</span>}
              {fight.description && <span>{fight.description}</span>}
              {(fight.time || fight.description) && <span className="text-flyers-dark-gray">|</span>}
              <Link
                to={`/game/${fight.Game?.GameID}`}
                className="text-flyers-light-gray hover:text-flyers-orange transition-colors"
              >
                {fight.Game?.HomeTeamDetails?.name} vs {fight.Game?.AwayTeamDetails?.name}
              </Link>
            </div>

            {/* YouTube Video Section */}
            <div className="mt-8">
              {videoId ? (
                <div className="bg-flyers-charcoal border border-flyers-dark-gray rounded-lg overflow-hidden">
                  <h3 className="font-heading text-lg font-bold uppercase tracking-wide text-flyers-light-gray px-5 py-3 border-b border-flyers-dark-gray">
                    Watch the Fight
                  </h3>
                  <div className="aspect-video">
                    <iframe
                      src={`https://www.youtube.com/embed/${videoId}`}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title="Fight video"
                    />
                  </div>
                </div>
              ) : (
                <div className="bg-flyers-charcoal border border-flyers-dark-gray rounded-lg p-8 flex flex-col items-center justify-center text-flyers-mid-gray">
                  <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="font-body text-sm">No video available yet</p>
                </div>
              )}
            </div>

            {/* Comments Section */}
            <CommentsSection fightID={fightID} isUserVoted={userVote !== null} />
          </div>

          {isOpenEditModal && (
            <UpdateFight
              close={() => setOpenEditModal(false)}
              fightID={fightID}
              fight={fight}
              isOpen={isOpenEditModal}
            />
          )}

          {isOpenDeleteModal && (
            <DeleteFight
              close={() => setOpenDeleteModal(false)}
              fightID={fightID}
              isOpen={isOpenDeleteModal}
              goBack={() => navigate(-1)}
            />
          )}
        </>
      )}
    </div>
  );
};
